/**
 * Effort — estimated and actual work, in days, at quarter-day granularity.
 *
 * See `Planning/EFFORT-TRACKING-2026-09-01.md`. Effort is DECOUPLED from a
 * task's date span: the span says when work happens, effort says how much work
 * it is. Only MAIN TASKS carry it (D3); subtasks carry neither field and the
 * write paths refuse them.
 *
 * ⚠️ ADMIN ONLY (D4, Bill 2026-09-01: "Strictly A, admins only, very
 * important"). Nothing in this module enforces that — the enforcement is the
 * strip in `projectRead.js` and the `adminView` gate in `mcpProjectShapes.js`.
 * This is arithmetic; do not add a permission check here and do not assume one
 * has happened.
 *
 * ⚠️ BYTE-IDENTICAL PAIR with `frontend/src/utils/effort.js`, enforced by
 * `tests/effort-module.test.js`. Edit BOTH copies. It therefore requires
 * NOTHING — a require would have to resolve under `/opt/nodejs/` and under
 * `frontend/src/utils/` alike, and the safest way to guarantee that is to have
 * none.
 *
 * The frontend-only display helpers (`effortLabel`, `isOverrun`, `variance`,
 * `coreMainTasks`) live in `frontend/src/utils/effortDisplay.js`, deliberately
 * NOT here: anything added to this file has to be added to both copies, and a
 * label format has no business on the backend.
 */

/** Quarter of a day. The granularity of every stored effort value. */
const EFFORT_STEP = 0.25;

/**
 * A typo guard, not a business limit (D21). It stops `400` being stored when
 * `4.0` was meant. A single main task needing over four working years is a
 * mistake rather than a plan.
 *
 * ⚠️ It bounds a TYPED value only. `sumEffort` never refuses a total, because a
 * rollup of many tasks may legitimately exceed it.
 */
const EFFORT_MAX = 999;

/** A 400-carrying error, in the shape the route handlers already map. */
function refuse(message) {
    const err = new Error(message);
    err.statusCode = 400;
    return err;
}

/**
 * Validate a typed effort value on its way to storage.
 *
 * @param {number|null|undefined|''} value  the typed value, or a clear
 * @param {string} field                    attribute name, for the message
 * @returns {number|null}                   the value, or null meaning "clear"
 * @throws  a 400-carrying error on anything invalid
 *
 * ⚠️ null is the CLEAR signal on the way IN. It is never a stored value on the
 * way out: each write path removes the attribute instead, because absent is the
 * single representation of "unestimated" and a stored null would be a second
 * one. The mechanism differs per location — see the plan's §5 table.
 */
function normaliseEffort(value, field) {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw refuse(`${field} must be a number`);
    }
    if (value < 0) {
        throw refuse(`${field} must not be negative`);
    }
    if (value > EFFORT_MAX) {
        throw refuse(`${field} must be ${EFFORT_MAX} or less`);
    }

    // ⚠️ NO EPSILON DANCE HERE, AND THAT IS DELIBERATE.
    //
    // organise-app's money validator needs a round-trip comparison because 0.1
    // is not representable in IEEE 754 and `0.1 * 100` is 10.000000000000002.
    // Quarters are different: 0.25, 0.5 and 0.75 are exact binary fractions, so
    // `value * 4` is exactly an integer for every legal value and exactly not
    // one for every illegal one. Copying the money check's machinery here would
    // suggest a fragility that does not exist.
    if (value * 4 !== Math.round(value * 4)) {
        throw refuse(`${field} must be a whole or quarter day`);
    }

    return value;
}

/**
 * Render a stored value for display: `4d`, `3.75d`, `0.25d`, `0d`.
 *
 * Absent renders as null, never as `0d` — an unestimated task and a task
 * estimated at zero are different facts (D6).
 */
function formatEffort(value) {
    if (value === null || value === undefined) return null;
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    // Number-to-string already trims: 4 -> "4", 3.75 -> "3.75", 0.5 -> "0.5".
    return `${value}d`;
}

/**
 * Sum one effort field across rows, and count how many rows lack it.
 *
 * ⛔ THE TWO ARE RETURNED TOGETHER, FROM ONE PASS, ON PURPOSE (D6). A total
 * without its unestimated count is a number that looks complete and is not:
 * "4.5 days" reads as the answer when the truth is "4.5 days plus three tasks
 * nobody has estimated". Computing them separately is how the two drift.
 *
 * @param {Array<Object>} rows  the rows to sum — CALLERS choose the set
 * @param {string} field        'effortEstimated' or 'effortActual'
 * @returns {{ total: number, unestimated: number }}
 *
 * ⚠️ This function does NOT decide which rows count. Filtering — main tasks
 * only, add-ins excluded, filters ignored — is `coreMainTasks`' job in
 * `effortDisplay.js` (D17/D22/D23). Handing it the wrong set is a caller bug
 * this cannot catch.
 */
function sumEffort(rows, field) {
    let quarters = 0;
    let unestimated = 0;

    for (const row of rows || []) {
        const value = row ? row[field] : undefined;
        if (typeof value === 'number' && Number.isFinite(value)) {
            // Summed in QUARTERS so the total is exact however many rows there
            // are: every legal value is a whole number of quarters, and integer
            // addition cannot accumulate the error repeated float addition can.
            quarters += Math.round(value * 4);
        } else {
            unestimated += 1;
        }
    }

    return { total: quarters / 4, unestimated };
}

module.exports = {
    EFFORT_STEP,
    EFFORT_MAX,
    normaliseEffort,
    formatEffort,
    sumEffort
};
